import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  buildContactLookupFromApi, processApiData, buildAllCampaigns,
  exportCampaignExcel, exportFounderWoWExcel, fmtInr, fmtPctStr, fmtPctDir
} from '../utils/dataEngine';
import { fetchClients, fetchUsers, fetchWeeklyMetrics } from '../utils/api';
import { BACKEND_BASE_URL, BACKEND_DIRECT_ACCESS_CODE } from '../config/backendConfig';
import { useCampaignData } from './CampaignDataContext';
import { BarChart3, Download, RotateCcw, Users } from 'lucide-react';
import { Button } from '../components/ui/button';

export const matchesKamSelection = (kam, selectedKams) => selectedKams.length === 0 || selectedKams.includes(kam);
export const toggleKamSelection = (selectedKams, kam) => {
  if (kam === 'All') return [];
  return selectedKams.includes(kam) ? selectedKams.filter(item => item !== kam) : [...selectedKams, kam];
};

function t7Of(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  d.setDate(d.getDate() - 7);
  return d.toISOString().split('T')[0];
}

function prevDayOf(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  d.setDate(d.getDate() - 1);
  return d.toISOString().split('T')[0];
}

function isMetricsEmpty(resp) {
  const metrics = resp?.metrics;
  if (!metrics || metrics.length === 0) return true;
  return metrics.every(r =>
    (r.overdue_value               || 0) === 0 &&
    (r.rejected_value              || 0) === 0 &&
    (r.short_grn_discrepancy_value || 0) === 0 &&
    (r.no_grn_gt30d_value          || 0) === 0
  );
}

// ── Connect screen ────────────────────────────────────────────────────────────
// Connects automatically on mount using the fixed backend config (no more
// per-browser manual entry) — the user only ever sees the email/password
// login gate now. Retries on demand if the fetch fails.
function ConnectScreen({ onConnect }) {
  const [error, setError] = useState(null);

  const connect = useCallback(async () => {
    if (!BACKEND_BASE_URL) {
      setError('Backend is not configured yet — set BACKEND_BASE_URL / BACKEND_DIRECT_ACCESS_CODE in src/config/backendConfig.js.');
      return;
    }
    setError(null);
    const authConfig = { directAccessCode: BACKEND_DIRECT_ACCESS_CODE };
    try {
      const todayDate = new Date().toISOString().split('T')[0];
      const [clientsData, usersData, todayMetrics] = await Promise.all([
        fetchClients(BACKEND_BASE_URL, authConfig),
        fetchUsers(BACKEND_BASE_URL, authConfig),
        fetchWeeklyMetrics(BACKEND_BASE_URL, authConfig, todayDate),
      ]);

      // If today's metrics haven't been calculated yet (all zeros), fall back to yesterday
      const date      = isMetricsEmpty(todayMetrics) ? prevDayOf(todayDate) : todayDate;
      const priorDate = t7Of(date);
      const [currentMetrics, priorMetrics] = await Promise.all([
        date === todayDate ? Promise.resolve(todayMetrics) : fetchWeeklyMetrics(BACKEND_BASE_URL, authConfig, date),
        fetchWeeklyMetrics(BACKEND_BASE_URL, authConfig, priorDate),
      ]);

      const contactLookup = buildContactLookupFromApi(usersData, clientsData);
      const { clientMetrics, todayStr, t7Str, newPlatClients } = processApiData(currentMetrics, priorMetrics);
      const campaigns  = buildAllCampaigns(clientMetrics, contactLookup, newPlatClients);
      const kamNames   = ['All', ...new Set(
        clientsData.flatMap(c => c.kams.map(k => k.first_name)).filter(Boolean)
      )].sort((a, b) => a === 'All' ? -1 : a.localeCompare(b));

      const isFallback = date !== todayDate;
      onConnect({ clientMetrics, contactLookup, campaigns, todayStr, t7Str, newPlatClients, kamNames, isFallback, todayDate });
    } catch (e) {
      setError(e.message);
    }
  }, [onConnect]);

  useEffect(() => { connect(); }, [connect]);

  return (
    <div className="upload-screen">
      <div className="upload-brand">
        <span className="brand-mark">A</span>
        <span className="brand-text">ARPFlow</span>
        <span className="brand-sub">Campaign Dashboard</span>
      </div>

      <div className={`connect-form ${error ? 'drop-error' : ''}`}>
        {!error ? (
          <div className="loading-state">
            <div className="spinner" />
            <p>Fetching data from API…</p>
          </div>
        ) : (
          <>
            <h2>Couldn't load dashboard data</h2>
            <p className="error-msg">{error}</p>
            <button className="file-btn connect-btn" onClick={connect}>Retry</button>
          </>
        )}
      </div>

      <div className="upload-info">
        <div className="info-item">
          <span className="info-icon">🌐</span>
          <div>
            <strong>Live API data</strong>
            <span>Clients · Users · Weekly metrics fetched on load</span>
          </div>
        </div>
        <div className="info-item">
          <span className="info-icon">🔄</span>
          <div>
            <strong>T-7 auto-fetched</strong>
            <span>Prior week metrics fetched automatically for WoW deltas</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const CAMPAIGNS = [
  { key: 'financeExec',    allKey: 'financeExecAll',    label: 'Finance Exec',     icon: '₹',  desc: 'FINANCE_CONTROLLER + FINANCE_EXECUTIVE' },
  { key: 'opsExec',        allKey: 'opsExecAll',        label: 'Ops Exec',          icon: '📦', desc: 'OPS_EXECUTIVE + OPERATIONS_HEAD' },
  { key: 'founderWoW',     allKey: 'founderWoWAll',     label: 'Founder WoW',       icon: '🎯', desc: 'FOUNDER_CEO + FOUNDER_CFO + FOUNDER_COO' },
  { key: 'founderSummary', allKey: 'founderSummaryAll', label: 'Founder Summary',   icon: '📋', desc: 'FOUNDER_CEO + FOUNDER_CFO + FOUNDER_COO — new platform clients only' },
];

// ── Metric card ───────────────────────────────────────────────────────────────
function MetricCell({ value, wow, hasT7 }) {
  const pct = wow !== null ? parseFloat(wow) : null;
  const dir = pct === null ? null : pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat';
  return (
    <div className="metric-cell">
      <span className="metric-val">{value}</span>
      {!hasT7 ? (
        <span className="wow-na">N/A</span>
      ) : (
        <span className={`wow-badge wow-${dir}`}>
          {fmtPctStr(wow)}
        </span>
      )}
    </div>
  );
}

// ── Summary table ─────────────────────────────────────────────────────────────
function SummaryPage({ clientMetrics, kamFilter }) {
  const [sort, setSort] = useState({ col: 'clientName', dir: 'asc' });

  const rows = useMemo(() => {
    return Object.values(clientMetrics).sort((a, b) => {
      const av = a[sort.col] ?? a.today?.[sort.col] ?? '';
      const bv = b[sort.col] ?? b.today?.[sort.col] ?? '';
      return sort.dir === 'asc'
        ? String(av).localeCompare(String(bv), undefined, { numeric: true })
        : String(bv).localeCompare(String(av), undefined, { numeric: true });
    });
  }, [clientMetrics, sort]);

  const th = (col, label) => (
    <th onClick={() => setSort(s => ({ col, dir: s.col === col && s.dir === 'asc' ? 'desc' : 'asc' }))}
        className={`sortable ${sort.col === col ? 'sorted' : ''}`}>
      {label} {sort.col === col ? (sort.dir === 'asc' ? '↑' : '↓') : ''}
    </th>
  );

  return (
    <div className="page-content">
      <div className="section-header">
        <h2>Client Metrics Summary</h2>
        <span className="row-count">{rows.length} clients</span>
      </div>
      <div className="table-wrap">
        <table className="summary-table">
          <thead>
            <tr>
              {th('clientName', 'Client')}
              <th className="col-group">Overdue</th>
              <th className="col-sub">WoW</th>
              <th className="col-group">Rejected</th>
              <th className="col-sub">WoW</th>
              <th className="col-group">No GRN 30+d</th>
              <th className="col-sub">WoW</th>
              <th className="col-group">Discrepancy</th>
              <th className="col-sub">New this week</th>
              <th className="col-sub">WoW</th>
              <th>New Plat?</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const t = r.today;
              const w = r.wow;
              return (
                <tr key={r.clientId} className={r.newPlatform ? 'row-new-plat' : ''}>
                  <td className="client-name-cell">
                    <span className="client-name">{r.clientName}</span>
                    <span className="client-id">#{r.clientId}</span>
                  </td>
                  <td className="num-cell">
                    <div className="num-primary">{fmtInr(t.overdueValue)}</div>
                    <div className="num-sub">{t.overdueCount} inv</div>
                  </td>
                  <td><WoWBadge pct={w.overdueValue} hasT7={r.hasT7Data} /></td>
                  <td className="num-cell">
                    <div className="num-primary">{t.rejectedValue > 0 ? fmtInr(t.rejectedValue) : '—'}</div>
                    <div className="num-sub">{t.rejectedCount > 0 ? `${t.rejectedCount} inv` : ''}</div>
                  </td>
                  <td><WoWBadge pct={w.rejectedValue} hasT7={r.hasT7Data} /></td>
                  <td className="num-cell">
                    <div className="num-primary">{fmtInr(t.noGrn30Value)}</div>
                    <div className="num-sub">{t.noGrn30Count} inv</div>
                  </td>
                  <td><WoWBadge pct={w.noGrn30Value} hasT7={r.hasT7Data} /></td>
                  <td className="num-cell">
                    <div className="num-primary">{t.shortGrnDiscrepancy > 0 ? fmtInr(t.shortGrnDiscrepancy) : '—'}</div>
                    <div className="num-sub">{t.shortGrnCount > 0 ? `${t.shortGrnCount} inv` : ''}</div>
                  </td>
                  <td className="num-cell">
                    {w.discAddedThisWeek > 0
                      ? <span className="disc-new">+{fmtInr(w.discAddedThisWeek)}</span>
                      : <span className="disc-none">—</span>}
                  </td>
                  <td><WoWBadge pct={w.shortGrnDiscrepancy} hasT7={r.hasT7Data} /></td>
                  <td>{r.newPlatform ? <span className="new-plat-tag">NEW</span> : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function WoWBadge({ pct, hasT7 }) {
  if (!hasT7) return <span className="wow-na">N/A</span>;
  if (pct === null) return <span className="wow-na">—</span>;
  const n = parseFloat(pct);
  const cls = n > 0 ? 'wow-up' : n < 0 ? 'wow-down' : 'wow-flat';
  return <span className={`wow-badge ${cls}`}>{fmtPctStr(pct)}</span>;
}

// ── Users page ────────────────────────────────────────────────────────────────
function UsersPage({ contactLookup, kamFilter }) {
  const [search, setSearch] = useState('');
  const [contactFilter, setContactFilter] = useState('all');
  const searchedUsers = useMemo(() => {
    return Object.values(contactLookup)
      .filter(u => !search || u.first_name?.toLowerCase().includes(search.toLowerCase()) ||
                   u.client_name?.toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => (a.client_name||'').localeCompare(b.client_name||'') || (a.first_name||'').localeCompare(b.first_name||''));
  }, [contactLookup, search]);

  const users = useMemo(() => searchedUsers.filter(user =>
    contactFilter === 'all' || (contactFilter === 'with' ? Boolean(user.contact_number) : !user.contact_number)
  ), [searchedUsers, contactFilter]);
  const withContact = searchedUsers.filter(u => u.contact_number).length;
  const missingContact = searchedUsers.length - withContact;
  const toggleContact = value => setContactFilter(current => current === value ? 'all' : value);

  return (
    <div className="page-content">
      <div className="section-header">
        <h2>User List</h2>
        <div className="header-right">
          <button type="button" className={`pill pill-filter pill-green ${contactFilter === 'with' ? 'active' : ''}`} aria-pressed={contactFilter === 'with'} onClick={() => toggleContact('with')}>{withContact} with contact</button>
          <button type="button" className={`pill pill-filter pill-grey ${contactFilter === 'missing' ? 'active' : ''}`} aria-pressed={contactFilter === 'missing'} onClick={() => toggleContact('missing')}>{missingContact} missing</button>
          <input className="search-input" placeholder="Search name or client…"
                 value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>
      <div className="table-wrap">
        <table className="users-table">
          <thead>
            <tr>
              <th>Name</th><th>Client</th><th>Designation</th>
              <th>Contact</th><th>Email</th><th>KAM</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id}>
                <td><span className="user-name">{u.first_name} {u.last_name}</span></td>
                <td><span className="client-chip">{u.client_name}</span></td>
                <td><span className="desig-chip">{u.designation}</span></td>
                <td>
                  {u.contact_number
                    ? <span className="contact-ok">{u.contact_number}</span>
                    : <span className="contact-missing">—</span>}
                </td>
                <td className="email-cell">{u.email || <span className="contact-missing">—</span>}</td>
                <td><span className={`kam-chip kam-${(u.kam||'none').toLowerCase()}`}>{u.kam || '—'}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Campaigns page ────────────────────────────────────────────────────────────
function CampaignsPage({ campaigns, kamFilter, kamNames }) {
  const [downloading, setDownloading] = useState(null);

  const handleDownload = (campaignKey, campaignLabel, kam) => {
    const key = `${campaignKey}_${kam}`;
    setDownloading(key);
    setTimeout(() => {
      const sourceRows = campaigns[campaignKey];
      const rows = Array.isArray(kam) ? sourceRows.filter(row => kam.includes(row.kam_name)) : sourceRows;
      const kamArg  = kam === 'All' || Array.isArray(kam) ? null : kam;
      const safeName = campaignLabel.replace(/\s+/g, '_');
      if (campaignKey === 'founderWoW' || campaignKey === 'founderWoWAll') {
        exportFounderWoWExcel(rows, safeName, kamArg);
      } else {
        exportCampaignExcel(rows, safeName, kamArg);
      }
      setDownloading(null);
    }, 300);
  };

  return (
    <div className="page-content">
      <div className="section-header">
        <h2>Campaign Downloads</h2>
        <span className="row-count">SheetWA-ready Excel files</span>
      </div>

      <div className="sheetwa-instructions">
        <span className="inst-icon">ℹ️</span>
        <span>Upload to SheetWA → set contact column = <code>contact_number</code> → Write Message → type <code>@Message</code> → Send</span>
      </div>

      <div className="campaigns-grid">
        {CAMPAIGNS.map(camp => {
          const rows = campaigns[camp.key] || [];
          const allKamsSelected = kamFilter.length === 0;
          const displayRows = allKamsSelected ? rows : rows.filter(r => kamFilter.includes(r.kam_name));
          const rowKamNames = [...new Set(rows.map(r => r.kam_name).filter(Boolean))];
          const visibleKamNames = (kamNames || rowKamNames).filter(k => rowKamNames.includes(k));
          const allKamNames = kamNames || visibleKamNames;
          const kamsAvailable = ['All', ...allKamNames];

          return (
            <div key={camp.key} className="campaign-card">
              <div className="campaign-card-header">
                <span className="camp-icon">{camp.icon}</span>
                <div>
                  <h3>{camp.label}</h3>
                  <p className="camp-desc">{camp.desc}</p>
                </div>
                <span className="camp-total">{displayRows.length} users</span>
              </div>

              <div className="camp-stats">
                {(allKamsSelected ? visibleKamNames : visibleKamNames.filter(k => kamFilter.includes(k))).map(k => {
                  const count = rows.filter(r => r.kam_name === k).length;
                  const withContact = rows.filter(r => r.kam_name === k && r.contact_number).length;
                  return (
                    <div key={k} className="camp-stat">
                      <span className={`kam-chip kam-${k.toLowerCase()}`}>{k}</span>
                      <span className="stat-num">{count}</span>
                      <span className="stat-contact">{withContact} contacts</span>
                    </div>
                  );
                })}
              </div>

              <div className="download-buttons">
                {(allKamsSelected ? kamsAvailable : kamFilter).map(kam => {
                  const count = kam === 'All' ? rows.length : rows.filter(r => r.kam_name === kam).length;
                  const key   = `${camp.key}_${kam}`;
                  const isLoading = downloading === key;
                  return (
                    <button key={kam} className={`dl-btn ${kam === 'All' ? 'dl-all' : 'dl-kam'}`}
                            onClick={() => handleDownload(camp.key, camp.label, kam)}
                            disabled={isLoading || count === 0}>
                      {isLoading ? '⏳' : '⬇'} {kam === 'All' ? 'All KAMs' : kam}
                      <span className="dl-count">{count}</span>
                    </button>
                  );
                })}
              </div>
              {camp.allKey && (() => {
                const allRows = campaigns[camp.allKey] || [];
                const kam = kamFilter.length ? kamFilter : null;
                const count = kam ? allRows.filter(r => kam.includes(r.kam_name)).length : allRows.length;
                const key = `${camp.allKey}_${kamFilter.join('|') || 'All'}`;
                const isLoading = downloading === key;
                return (
                  <div className="download-buttons download-all-types">
                    <button className="dl-btn dl-all-types"
                            onClick={() => handleDownload(camp.allKey, `${camp.label}_AllTypes`, kam || 'All')}
                            disabled={isLoading || count === 0}>
                      {isLoading ? '⏳' : '⬇'} All User Types {kam ? `· ${kam}` : ''}
                      <span className="dl-count">{count}</span>
                    </button>
                  </div>
                );
              })()}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────────
export default function CampaignDashboard() {
  const { appData, setAppData, kamFilter, setKamFilter, activeTab, setActiveTab } = useCampaignData();

  const contactLookup = appData?.contactLookup || {};
  const clientMetrics = appData?.clientMetrics || {};

  const filteredContactLookup = useMemo(() => {
    const users = Object.values(contactLookup).filter(u => matchesKamSelection(u.kam, kamFilter));
    return Object.fromEntries(users.map(u => [u.id, u]));
  }, [contactLookup, kamFilter]);

  const filteredClientMetrics = useMemo(() => {
    if (kamFilter.length === 0) return clientMetrics;
    const clientIds = new Set(
      Object.values(filteredContactLookup)
        .map(u => parseInt(u.client_uid, 10))
        .filter(Number.isFinite)
    );
    return Object.fromEntries(
      Object.entries(clientMetrics).filter(([cid]) => clientIds.has(parseInt(cid, 10)))
    );
  }, [clientMetrics, filteredContactLookup, kamFilter]);

  if (!appData) {
    // Note: kamFilter is intentionally left untouched here — it already carries
    // either the logged-in KAM's default (see CampaignDataContext) or whatever
    // the user had selected before a manual Reconnect. Resetting it on every
    // connect used to silently wipe out that default the instant data loaded.
    return <ConnectScreen onConnect={setAppData} />;
  }

  const { campaigns, todayStr, t7Str, kamNames, isFallback, todayDate } = appData;
  const KAMS        = (kamNames || []).filter(kam => kam !== 'All');
  const toggleKam = kam => {
    setKamFilter(selected => toggleKamSelection(selected, kam));
  };
  const clientCount = Object.keys(filteredClientMetrics).length;
  const userCount   = Object.keys(filteredContactLookup).length;

  return (
    <div className="app">
      {/* ── Sidebar ── */}
      <aside className="sidebar">
        <div className="sidebar-brand">
          <span className="brand-mark">A</span>
          <div>
            <span className="brand-text">ARPFlow</span>
            <span className="brand-sub">Campaigns</span>
          </div>
        </div>

        <div className="data-meta">
          <div className="meta-item">
            <span className="meta-label">Week of</span>
            <span className="meta-val">{new Date(todayStr).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}</span>
          </div>
          <div className="meta-item">
            <span className="meta-label">vs. prev week</span>
            <span className="meta-val">{new Date(t7Str).toLocaleDateString('en-IN',{day:'numeric',month:'short'})}</span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Source</span>
            <span className="meta-file">API</span>
          </div>
          {isFallback && (
            <div className="meta-fallback-notice">
              {new Date(todayDate).toLocaleDateString('en-IN',{day:'numeric',month:'short'})} not calculated yet — showing {new Date(todayStr).toLocaleDateString('en-IN',{day:'numeric',month:'short'})}
            </div>
          )}
        </div>

        <nav className="sidebar-nav">
          {[
            { key: 'summary',   label: 'Summary',   icon: '📊' },
            { key: 'users',     label: 'Users',      icon: '👥' },
            { key: 'campaigns', label: 'Campaigns',  icon: '📨' },
          ].map(item => (
            <button key={item.key}
                    className={`nav-item ${activeTab === item.key ? 'active' : ''}`}
                    onClick={() => setActiveTab(item.key)}>
              <span className="nav-icon">{item.icon}</span>
              <span>{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="sidebar-stats">
          <div className="stat-row"><span>{clientCount}</span><label>Clients</label></div>
          <div className="stat-row"><span>{userCount}</span><label>Users</label></div>
        </div>

        <button className="reload-btn" onClick={() => setAppData(null)}>
          ↩ Reconnect
        </button>
      </aside>

      {/* ── Main ── */}
      <main className="main">
        <div className="campaign-page-header">
          <div><span className="eyebrow">Campaigns</span><h1>Campaign performance</h1><p>Monitor weekly communication metrics and prepare campaign exports.</p></div>
          <Button variant="outline" size="sm" onClick={() => setAppData(null)}><RotateCcw size={15} />Reconnect</Button>
        </div>
        <div className="campaign-tabs" role="tablist" aria-label="Campaign dashboard views">
          {[{ key:'summary',label:'Summary',icon:BarChart3 },{ key:'users',label:'Users',icon:Users },{ key:'campaigns',label:'Campaigns',icon:Download }].map(item => {
            const Icon = item.icon;
            return <Button key={item.key} role="tab" aria-selected={activeTab === item.key} variant={activeTab === item.key ? 'secondary' : 'ghost'} size="sm" onClick={() => setActiveTab(item.key)}><Icon size={15}/>{item.label}</Button>;
          })}
        </div>
        {/* Top bar */}
        <header className="topbar">
          <div className="kam-filter">
            {['All', ...KAMS].map(k => {
              const active = k === 'All' ? kamFilter.length === 0 : kamFilter.includes(k);
              return (
              <button key={k}
                      className={`kam-btn kam-btn-${k.toLowerCase()} ${active ? 'active' : ''}`}
                      aria-pressed={active}
                      onClick={() => toggleKam(k)}>
                {k}
              </button>
              );
            })}
          </div>
          <span className="topbar-hint">Filter by KAM across all views</span>
        </header>

        {activeTab === 'summary'   && <SummaryPage   clientMetrics={filteredClientMetrics} kamFilter={kamFilter} />}
        {activeTab === 'users'     && <UsersPage     contactLookup={filteredContactLookup} kamFilter={kamFilter} />}
        {activeTab === 'campaigns' && <CampaignsPage campaigns={campaigns} kamFilter={kamFilter} kamNames={KAMS} />}
      </main>
    </div>
  );
}
